import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft, Calendar, DollarSign, Edit, Clock, Loader2, FileText,
  AlertCircle, Building, Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DateCell } from "@/components/DateCell";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface CreditMemo {
  id: string;
  creditMemoNumber: string;
  memoType: string;
  status: string;
  accountId: string | null;
  vendorId: string | null;
  originalInvoiceId: string | null;
  originalApInvoiceId: string | null;
  memoDate: string;
  reason: string | null;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  notes: string | null;
  createdAt: string;
  account?: { id: string; name: string };
  vendor?: { id: string; name: string };
  originalInvoice?: { id: string; invoiceNumber: string };
  originalApInvoice?: { id: string; invoiceNumber: string };
}

const CREDIT_MEMO_STATUSES = [
  { value: "open", label: "Open" },
  { value: "applied", label: "Applied" },
  { value: "void", label: "Void" },
];

function formatStatusLabel(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function CreditMemoDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/credit-memos/:id");
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("open");
  const [editNotes, setEditNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const id = params?.id;

  const { data: memo, isLoading, error } = useQuery<CreditMemo>({
    queryKey: ["/api/credit-memos", id],
    queryFn: () => api.get<CreditMemo>(`/credit-memos/${id}`, sessionId),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { status: string; notes: string | null }) =>
      api.patch<CreditMemo>(`/credit-memos/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/credit-memos", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/credit-memos"] });
      toast({ title: "Credit memo updated" });
      setEditOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const handleEditOpen = () => {
    if (memo) {
      setEditStatus(memo.status || "open");
      setEditNotes(memo.notes || "");
    }
    setEditOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ status: editStatus, notes: editNotes || null });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !memo) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Credit memo not found</p>
        <Button variant="outline" onClick={() => setLocation("/credit-memos")} data-testid="button-back-to-credit-memos">
          Back to Credit Memos
        </Button>
      </div>
    );
  }

  const statusDotColor = (() => {
    switch (memo.status) {
      case "open": return "bg-blue-500";
      case "applied": return "bg-green-500";
      case "void": return "bg-red-500";
      default: return "bg-gray-400";
    }
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/credit-memos")}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-back-to-credit-memos"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-credit-memo-number">
                {memo.creditMemoNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {memo.memoType === "AP" && memo.vendor ? (
                <button
                  onClick={() => setLocation(`/vendors/${memo.vendor!.id}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-credit-memo-party"
                >
                  {memo.vendor.name}
                </button>
              ) : memo.account ? (
                <button
                  onClick={() => setLocation(`/accounts/${memo.account!.id}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-credit-memo-party"
                >
                  {memo.account.name}
                </button>
              ) : null}
              {(memo.account || (memo.memoType === "AP" && memo.vendor)) && (
                <span className="text-muted-foreground font-normal">/</span>
              )}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span data-testid="text-credit-memo-status">{formatStatusLabel(memo.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen} data-testid="button-edit-credit-memo">
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto" data-testid="mini-cards-strip">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor)} />
              <span className="text-sm font-medium capitalize">{memo.status}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Total</span>
              <p className="text-sm font-medium" data-testid="text-total-amount">
                ${Number(memo.totalAmount || 0).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Memo Date</span>
              <p className="text-sm font-medium" data-testid="text-memo-date">
                <DateCell value={memo.memoDate} fallback="—" />
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Created</span>
              <p className="text-sm font-medium" data-testid="text-created-at">
                <DateCell value={memo.createdAt} fallback="—" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Details
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Reason</span>
              <span className="text-sm font-medium" data-testid="text-reason">{memo.reason || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Subtotal</span>
              <span className="text-sm font-medium">${Number(memo.subtotal || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Tax</span>
              <span className="text-sm font-medium">${Number(memo.taxAmount || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              {memo.memoType === "AR" ? (
                <Building className="h-4 w-4 text-emerald-500" />
              ) : (
                <Truck className="h-4 w-4 text-emerald-500" />
              )}
              {memo.memoType === "AR" ? "Account" : "Vendor"}
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {memo.memoType === "AR" && memo.account && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Account</span>
                <button
                  onClick={() => memo.accountId && setLocation(`/accounts/${memo.accountId}`)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-account"
                >
                  {memo.account.name}
                </button>
              </div>
            )}
            {memo.memoType === "AP" && memo.vendor && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Vendor</span>
                <button
                  onClick={() => memo.vendorId && setLocation(`/vendors/${memo.vendorId}`)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-vendor"
                >
                  {memo.vendor.name}
                </button>
              </div>
            )}
            {memo.originalInvoiceId && memo.originalInvoice && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Original Invoice</span>
                <button
                  onClick={() => setLocation(`/invoices/${memo.originalInvoiceId}`)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-original-invoice"
                >
                  {memo.originalInvoice.invoiceNumber}
                </button>
              </div>
            )}
            {memo.originalApInvoiceId && memo.originalApInvoice && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Original Vendor Invoice</span>
                <button
                  onClick={() => setLocation(`/ap-invoices/${memo.originalApInvoiceId}`)}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-original-ap-invoice"
                >
                  {memo.originalApInvoice.invoiceNumber}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm lg:col-span-2">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4">
            {memo.notes ? (
              <p className="text-sm whitespace-pre-wrap" data-testid="text-notes">{memo.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No notes added.</p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Credit Memo</DialogTitle>
            <DialogDescription>Update the credit memo information below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status *</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="select-edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_MEMO_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="Memo notes..."
                className="min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="textarea-edit-notes"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} data-testid="button-cancel-edit">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-credit-memo">
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
